import './style.css';
import 'ol/ol.css';

import OLMap from 'ol/Map';
import View from 'ol/View';
import { defaults as defaultControls } from 'ol/control/defaults';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import CircleGeom from 'ol/geom/Circle';
import Style from 'ol/style/Style';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import { fromLonLat, toLonLat } from 'ol/proj';
import type BaseLayer from 'ol/layer/Base';

import { registerProjections, fromLonLatToSJTSK } from './projections';
import { buildLayer } from './layers';
import { source, needsEpoch, newestEpochId, newestLocalEpochId } from './catalogue';
import { localVersions } from './wayback';
import {
  activeEntry,
  addOverlay,
  getState,
  initFromHash,
  removeOverlay,
  setBase,
  setBaseTime,
  setCompare,
  setOpacity,
  setOverlayTime,
  setView,
  subscribe,
} from './state';
import type { StackEntry } from './types';
import type { AppState } from './state';
import { Picker, noteRecent } from './ui/picker';
import { InfoSheet } from './ui/info';
import { TimeStrip } from './ui/timestrip';
import { Compare } from './ui/compare';
import { Hud } from './ui/hud';
import { ExportPanel } from './ui/exportpanel';
import { el, qs } from './ui/dom';

registerProjections();

const app = qs('#app');
const mapEl = el('div', { class: 'map', attrs: { id: 'map' } });
app.append(mapEl);

const view = new View({
  center: fromLonLat([14.4213, 50.0875]),
  zoom: 14,
  maxZoom: 22,
  minZoom: 5,
  constrainResolution: false,
});

const map = new OLMap({
  target: mapEl,
  view,
  controls: defaultControls({ attribution: false, zoom: false, rotate: false }),
});

// ------------------------------------------------------------------ location

const locationSource = new VectorSource();
const locationLayer = new VectorLayer({
  source: locationSource,
  zIndex: 900,
  style: [
    new Style({
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({ color: '#ffb340' }),
        stroke: new Stroke({ color: '#1b1b1f', width: 2 }),
      }),
    }),
    new Style({
      fill: new Fill({ color: 'rgba(255, 179, 64, 0.14)' }),
      stroke: new Stroke({ color: 'rgba(255, 179, 64, 0.5)', width: 1 }),
    }),
  ],
});
locationLayer.set('chrome', true);
map.addLayer(locationLayer);

function locate(): void {
  if (!navigator.geolocation) return;
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const coord = fromLonLat([pos.coords.longitude, pos.coords.latitude]);
      locationSource.clear();
      locationSource.addFeature(new Feature(new Point(coord)));
      locationSource.addFeature(new Feature(new CircleGeom(coord, Math.max(pos.coords.accuracy, 8))));
      view.animate({ center: coord, zoom: Math.max(view.getZoom() ?? 14, 17), duration: 400 });
    },
    () => undefined,
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 },
  );
}

// -------------------------------------------------------------- layer wiring

const layerCache = new Map<string, BaseLayer>();

function keyOf(entry: StackEntry): string {
  return `${entry.sourceId}|${entry.timeId ?? ''}`;
}

function layerFor(entry: StackEntry): BaseLayer | undefined {
  const def = source(entry.sourceId);
  if (!def) return undefined;
  // Epoch still unknown (a remote epoch list is loading) — render nothing rather
  // than ask the service for a literal {time}.
  if (needsEpoch(def) && !entry.timeId) return undefined;
  const key = keyOf(entry);
  let layer = layerCache.get(key);
  if (!layer) {
    layer = buildLayer(def, entry.timeId, entry.opacity);
    layerCache.set(key, layer);
  }
  return layer;
}

function syncLayers(state: AppState): BaseLayer | undefined {
  const wanted: BaseLayer[] = [];
  const entries = [state.base, ...state.overlays];

  entries.forEach((entry, index) => {
    const layer = layerFor(entry);
    if (!layer) return;
    layer.setOpacity(entry.opacity);
    layer.setZIndex(index);
    wanted.push(layer);
    if (!map.getLayers().getArray().includes(layer)) map.addLayer(layer);
  });

  for (const layer of [...map.getLayers().getArray()]) {
    // Chrome layers (location marker, export footprint) are not part of the stack.
    if (layer.get('chrome')) continue;
    if (!wanted.includes(layer)) map.removeLayer(layer);
  }
  return wanted[wanted.length - 1];
}

// ------------------------------------------------------------------------ ui

const compare = new Compare(map);
mapEl.append(compare.handle);

const info = new InfoSheet();
const timestrip = new TimeStrip((timeId) => {
  const entry = activeEntry();
  if (entry === getState().base) setBaseTime(timeId);
  else setOverlayTime(entry.sourceId, timeId);
});

const picker = new Picker({
  onBase: (def) => {
    setBase(def.id, newestLocalEpochId(def));
    picker.sheet.setSnap('closed');
  },
  onOverlay: (def) => {
    const state = getState();
    if (state.overlays.some((o) => o.sourceId === def.id)) removeOverlay(def.id);
    else {
      addOverlay(def.id, newestLocalEpochId(def));
    }
    picker.render();
  },
  onInfo: (def) => info.show(def, undefined),
});

const exportPanel = new ExportPanel(map, () => {
  const entry = activeEntry();
  const def = source(entry.sourceId);
  return def ? { def, timeId: entry.timeId } : undefined;
});

const hud = new Hud(
  {
    onOpenPicker: () => picker.open(),
    onLocate: locate,
    onToggleCompare: () => setCompare(!getState().compare),
    onExport: () => {
      picker.sheet.setSnap('closed');
      info.sheet.setSnap('closed');
      exportPanel.toggle();
      hud.setExportActive(exportPanel.isActive);
    },
    onOpacity: setOpacity,
    onRemoveOverlay: (id) => {
      removeOverlay(id);
      picker.render();
    },
    onInfo: (id, timeId) => {
      const def = source(id);
      if (def) info.show(def, timeId);
    },
  },
  timestrip.root,
);

app.append(hud.root, exportPanel.root, picker.sheet.root, info.sheet.root);

/** Fill in a missing epoch for any stack entry that cannot be addressed without one. */
function ensureEpochs(state: AppState): void {
  const pending: { entry: StackEntry; isBase: boolean }[] = [
    { entry: state.base, isBase: true },
    ...state.overlays.map((entry) => ({ entry, isBase: false })),
  ].filter(({ entry }) => {
    const def = source(entry.sourceId);
    return def && needsEpoch(def) && !entry.timeId;
  });

  for (const { entry, isBase } of pending) {
    const def = source(entry.sourceId)!;
    const resolve =
      def.timesMode === 'wayback-local'
        ? localVersions(centerOf(), view.getZoom() ?? 14).then((t) => t[t.length - 1]?.id)
        : newestEpochId(def);
    void resolve.then((id) => {
      if (!id) return;
      if (isBase) {
        if (getState().base.sourceId === def.id && !getState().base.timeId) setBaseTime(id);
      } else {
        const current = getState().overlays.find((o) => o.sourceId === def.id);
        if (current && !current.timeId) setOverlayTime(def.id, id);
      }
    });
  }
}

// --------------------------------------------------------------- reactivity

function centerOf(): [number, number] {
  const c = view.getCenter() ?? [0, 0];
  return [c[0], c[1]];
}

let lastTopLayer: BaseLayer | undefined;

subscribe((state, reason) => {
  if (reason === 'view') return;

  ensureEpochs(state);
  const top = syncLayers(state);
  lastTopLayer = top;

  // An opacity change is already applied by syncLayers, and the slider that caused
  // it is showing the right value — re-rendering the HUD would only yank it away
  // mid-drag.
  if (reason === 'opacity') return;

  hud.update(state);
  noteRecent(state.base.sourceId);

  const entry = activeEntry();
  const def = source(entry.sourceId);
  if (def && (def.kind === 'collection' || def.timesMode)) {
    void timestrip.show(def, entry.timeId, centerOf(), view.getZoom() ?? 14);
  } else {
    timestrip.hide();
  }

  compare.enable(state.compare && state.overlays.length ? top : undefined);

  if (reason === 'init') {
    view.setCenter(fromLonLat([state.view.lon, state.view.lat]));
    view.setZoom(state.view.zoom);
  }
});

function updateScale(): void {
  const resolution = view.getResolution();
  if (!resolution) return;
  const [, lat] = toLonLat(centerOf());
  // EPSG:3857 resolutions are inflated by latitude; correct to ground metres.
  hud.setScale(resolution * Math.cos((lat * Math.PI) / 180));
}

map.on('moveend', () => {
  const [lon, lat] = toLonLat(centerOf());
  setView({ lon, lat, zoom: view.getZoom() ?? 14 });
  timestrip.setCenter(centerOf(), view.getZoom() ?? 14);
  updateScale();
});
view.on('change:resolution', updateScale);

mapEl.addEventListener('contextmenu', (ev) => {
  ev.preventDefault();
  const coord = map.getEventCoordinate(ev);
  const [lon, lat] = toLonLat(coord);
  hud.showCoords(lon, lat, fromLonLatToSJTSK(lon, lat));
});

map.on('click', () => {
  if (exportPanel.isActive) return;
  if (picker.sheet.currentSnap !== 'closed') picker.sheet.setSnap('closed');
  if (info.sheet.currentSnap !== 'closed') info.sheet.setSnap('closed');
});

initFromHash();
updateScale();
// The stack is in place; make sure the compare clip targets the right layer.
compare.enable(getState().compare && getState().overlays.length ? lastTopLayer : undefined);

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('./sw.js').catch(() => undefined);
  });
}
